import React from 'react';

type ConversationTextAreaProps = {
    value: string;
    onChange: (value: string) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    placeholder?: string;
    disabled?: boolean;
    maxLength: number;
    rows?: number;
    maxRows?: number;
    showCharacterCount?: boolean;
    className?: string;
    variant?: 'light' | 'dark';
};

export const ConversationTextArea = ({
    value,
    onChange,
    onKeyDown,
    placeholder,
    disabled,
    maxLength,
    rows = 1,
    maxRows = 3,
    showCharacterCount = false,
    className,
    variant = 'light',
}: ConversationTextAreaProps) => {
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    React.useLayoutEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const style = window.getComputedStyle(textarea);
        const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);

        textarea.style.height = 'auto';
        textarea.rows = 1;
        const singleLineHeight = textarea.clientHeight;
        const lineHeight = singleLineHeight - padding;

        if (lineHeight <= 0) {
            textarea.rows = rows;
            return;
        }

        const minHeight = lineHeight * rows + padding;
        const maxHeight = lineHeight * maxRows + padding;

        const newHeight = Math.max(minHeight, Math.min(textarea.scrollHeight, maxHeight));
        textarea.style.height = `${newHeight}px`;
        textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
        textarea.rows = rows;
    }, [value, rows, maxRows]);

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange(event.target.value.slice(0, maxLength));
    };

    const inputClasses = variant === 'dark'
        ? 'w-full p-2 rounded-none focus:outline-none resize-none bg-gray-800 text-white border border-gray-600 placeholder-gray-400 focus:border-gray-500 disabled:bg-gray-700'
        : 'w-full p-2 rounded focus:outline-none resize-none border border-gray-300 focus:border-gray-500 disabled:bg-gray-100';
    const countClasses = variant === 'dark'
        ? 'text-right text-xs text-gray-300 mt-1'
        : 'text-right text-xs text-gray-500 mt-1';

    return (
        <div className={`w-full min-w-0 ${className ?? ''} text-sm`}>
            <textarea
                ref={textareaRef}
                className={inputClasses}
                rows={rows}
                value={value}
                onChange={handleChange}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                disabled={disabled}
                maxLength={maxLength}
            />
            {showCharacterCount && (
                <div className={countClasses}>
                    {value.length}/{maxLength} characters
                </div>
            )}
        </div>
    );
};
